// @flow

import React, { useEffect, type Node } from 'react';
import { getImplementedTraits } from '@tectonics/look-and-feel';
import { type Country } from '@tectonics/prs/country';
import { getFeatureByName, getSupportedTraits, type Feature } from '@tectonics/prs/feature';
import { getCountry, getOneStepData, useIncomingPayment } from '@tectonics/prs/incoming-payment';
import {
  getOverwrittenPaymentTypeTitle,
  getOverwrittenPaymentTypesTitles,
  type OverwrittenPaymentTypesTitles,
} from '@tectonics/prs/one-step';
import { useAllFeatures, usePaymentSpec } from '@tectonics/prs/payment-spec';
import { getSpecImplementedPaymentMethods } from '@tectonics/prs/support';
import { useEventBusSafely } from '@tectonics/public-event-bus';
import { Empty } from '@tectonics/components';
import '@tectonics-extra/songbird';
import laf from '../../../index';

export function AvailablePaymentMethods(): Node {
  const paymentSpec = usePaymentSpec();
  const features = useAllFeatures();
  const incomingPayment = useIncomingPayment();
  const eventBus = useEventBusSafely();
  const incomingOneStepData = getOneStepData(incomingPayment);
  const overwrittenTitles = incomingOneStepData
    ? getOverwrittenPaymentTypesTitles(incomingOneStepData)
    : null;

  useEffect(() => {
    const country = getCountry(incomingPayment);
    const paymentMethods = getSpecImplementedPaymentMethods(paymentSpec, laf.prsSupport)
      .map(code => createPaymentMethodData({ code, country, features }))
      .map(overwriteTitle(overwrittenTitles, country))
      .filter(paymentMethod => paymentMethod != null);

    eventBus.trigger('paymentMethods:available', paymentMethods);
  }, [incomingPayment, features, paymentSpec, eventBus]);

  return <Empty />;
}

type PaymentMethodData = {|
  +code: string;
  +title: string;
  +brands: ImmutableArray<string>;
|};

type CreatePaymentMethodDataArgs = {|
  code: string,
  country: Country,
  features: ImmutableArray<Feature>,
|};

function createPaymentMethodData({ code, country, features }: CreatePaymentMethodDataArgs): PaymentMethodData | null {
  switch (code) {
    case 'boleto':
      return {
        code: 'boleto',
        title: 'Boleto Bancário via EBANX',
        brands: [],
      };
    case 'cash':
      return {
        code: 'cash',
        title: getCashTitle(country),
        brands: [],
      };
    case 'creditcard':
      return {
        code: 'creditcard',
        title: getCreditCardTitle(country),
        brands: getCreditCardBrands(features),
      };
    case 'debitcard':
      return {
        code: 'debitcard',
        title: getDebitCardTitle(country),
        brands: getDebitCardBrands(features),
      };
    case 'ewallet': {
      const brands = getEwalletsBrands(features);
      if (!brands || !brands.length) return null;
      return {
        code: 'ewallet',
        title: getEwalletTitle(country),
        brands,
      };
    }
    case 'oxxo':
      return {
        code: 'oxxo',
        title: 'OXXO vía EBANX',
        brands: [],
      };
    case 'pix':
      return {
        code: 'pix',
        title: 'Pix via EBANX',
        brands: [],
      };
    case 'spei':
      return {
        code: 'spei',
        title: 'SPEI vía EBANX',
        brands: [],
      };
    case 'webpay':
      return {
        code: 'webpay',
        title: 'Webpay vía EBANX',
        brands: [],
      };
    case 'externalpayprovider':
      return {
        code: 'webpay-rest',
        title: 'Webpay vía EBANX',
        brands: [],
      };
    case 'multicaja':
      return {
        code: 'multicaja',
        title: 'Multicaja vía EBANX',
        brands: [],
      };
    case 'sencillito':
      return {
        code: 'sencillito',
        title: 'Sencillito vía EBANX',
        brands: [],
      };
    case 'servipag':
      return {
        code: 'servipag',
        title: 'Servipag vía EBANX',
        brands: [],
      };
    case 'eft':
      return {
        code: 'eft',
        title: getEftTitle(country),
        brands: [],
      };
    default:
      throw new Error(`Payment method not mapped "${code}"`);
  }
}

function getCreditCardTitle(country: Country): string {
  switch (country) {
    case 'ar':
      return 'Tarjeta de Crédito en Argentina vía EBANX';
    case 'br':
      return 'Cartão de Crédito via EBANX';
    case 'cl':
      return 'Tarjeta de Crédito en Chile vía EBANX';
    case 'co':
      return 'Tarjeta de Crédito o Débito en Colombia vía EBANX';
    case 'mx':
      return 'Tarjeta de Crédito o Débito en México vía EBANX';
    case 'uy':
      return 'Tarjeta de Crédito en Uruguay vía EBANX';
    case 'py':
      return 'Tarjeta de Crédito en Paraguay vía EBANX';
    default:
      throw new Error(`Credit card title not available for "${country}"`);
  }
}

function getCreditCardBrands(features: ImmutableArray<Feature>): ImmutableArray<string> {
  const feature = getFeatureByName(features, 'display_card_accepts_brand');
  if (feature) {
    return getSupportedTraits(feature);
  }
  return [];
}

function getDebitCardTitle(country: Country): string {
  switch (country) {
    case 'ar':
      return 'Tarjeta de Débito en Argentina vía EBANX';
    case 'br':
      return 'Cartão de Débito via EBANX';
    case 'mx':
      return 'Tarjeta de Débito en México vía EBANX';
    case 'uy':
      return 'Tarjeta de Débito en Uruguay vía EBANX';
    default:
      throw new Error(`Debit card title not available for "${country}"`);
  }
}

function getDebitCardBrands(features: ImmutableArray<Feature>): ImmutableArray<string> {
  const feature = getFeatureByName(features, 'display_debit_card_accepts_brand');
  if (feature) {
    return getSupportedTraits(feature);
  }
  return [];
}

function getEwalletTitle(country: Country): string {
  switch (country) {
    case 'br':
      return 'Carteiras Digitais via EBANX';
    case 'co':
      return 'NEQUI vía EBANX';
    case 'ar':
    case 'mx':
      return 'MercadoPago via EBANX';
    default:
      throw new Error(`Ewallet title not available for "${country}"`);
  }
}

function getCashTitle(country: Country): string {
  switch (country) {
    case 'ar':
      return 'Cupones de pago vía EBANX';
    case 'co':
      return 'Efecty vía EBANX';
    default:
      throw new Error(`Cash title not available for "${country}"`);
  }
}

function getEwalletsBrands(features: ImmutableArray<Feature>): ImmutableArray<string> {
  return getImplementedTraits(laf, 'display_ewallet_accepts_brand', features);
}

function getEftTitle(country: Country): string {
  switch (country) {
    case 'co':
      return 'Pago con PSE';
    default:
      throw new Error(`Transparent checkout for EFT not available for "${country}"`);
  }
}

function overwriteTitle(
  overwrittenTitles: ?OverwrittenPaymentTypesTitles,
  countryCode: Country,
): (?PaymentMethodData) => ?PaymentMethodData {
  return (data: ?PaymentMethodData) => {
    if (data == null || overwrittenTitles == null) {
      return data;
    }
    return {
      ...data,
      title: getOverwrittenPaymentTypeTitle({
        titles: overwrittenTitles,
        paymentMethodCode: data.code,
        countryCode: countryCode,
        defaultTitle: data.title,
      }),
    };
  };
}
